import { Text } from "@imminently/react/ui/core/text";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { merge, set } from "lodash";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import {
  Await,
  NavLink,
  defer,
  useAsyncError,
  useAsyncValue,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import ReactToPrint from "react-to-print";

import {
  PatientSplitActions,
  PatientObservationSplitActions,
  SaveObservationsSplitActions,
} from "@/components/PatientActionButtons";
import { PatientTasks } from "@/components/PatientTasks";
import {
  CompareIcon,
  HeartIcon,
  ArrowleftIcon,
} from "@/assets/icons";
import { WithObsTabsCtx } from "@/components/ObservationChart/ObservationTabs_ctx";
import PatientActivitiesSection from "@/components/Patient/PatientActivitiesSection";
import PatientDetailsTabs from "@/components/Patient/PatientDetailsTabs";
import { PatientHeader } from "@/components/Patient/PatientHeader";
import PatientObservationTabs from "@/components/Patient/PatientObservationsTabs";
import PatientEncounterComment from "@/components/Patient/PatientEncounterComment";
import PatientNextObservation from "@/components/Patient/PatientNextObservation";
import { PatientDetailBlock } from "@/components/PatientDetailBlock";
import { PatientNotes } from "@/components/Patient/PatientNotes";
import TabSection, { TabSubSection } from "@/components/TabSection";
import { scrollable } from "@/components/scrollable";
import { usePatientRiskModal } from "@/modals/patientRisk";
import { AppLoading } from "@/pages/app-loading";
import {
  ClinicalRisk,
  type PatientEncounter,
  type Task,
  TaskType,
} from "@/resources/types";
import { encounterService } from "@/services";
import { isActAsUnauthorisedError } from "@/global";
import { css, type Theme } from "@/theme";
import { mockEncounter01 } from "@/util/printable-obschart/mocks/mockEncounter01";
import { WORK_OFFLINE } from "@/resources/constants";

export type PatientEncounterChangeListener = (
  path: string,
  value: any,
  readPath?: string
) => void;

export enum TabNames {
  RECORD = "patient-record",
  OBS_CHART = "observations-chart",
}

const styles = {
  container: (theme: Theme) => css`
    background-color: ${theme.palette.primary[50]};
    // Reduce padding to compensate for scrollbar width
    // TODO this only works on chrome, breaks firefox,
    // used to use overflow: overlay but that is deprecated
    // and has been removed from chrome
    padding-right: 1.5rem;
  `,
  cardBadge: (theme: Theme) => css`
    font-family: lato;
    font-size: 12px;
    font-weight: 700;
    color: ${theme.palette.common.white};
    display: inline;
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;
    background-color: ${theme.palette.grey[900]};
  `,
  subHeading: (theme: Theme) => css`
    font-family: lato;
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;
    color: ${theme.palette.primary[500]};
  `,
  bulletPoint: css`
    font-size: 10px;
    display: inline-block;
    padding: 0px 15px 5px 15px;
  `,
  backButton: css`
    font-family: "Lato";
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #354176;
    background: none;

    &:hover {
      background: none;
      box-shadow: none;
    }
  `,
  tabHeader: {
    bgcolor: "primary.100",
    height: "48px",
  },
  tabButtonsWrapper: {
    position: "absolute",
    right: "2.5rem",
    top: "-1.25rem",
    zIndex: 10,
  },
};

export const getPatientRoute = (id: string) => `/patients/${id}`;

export const observationsLoader = async ({ params }: any) => {
  if (WORK_OFFLINE) {
    return mockEncounter01 as unknown as PatientEncounter;
  }
  const { id } = params;
  const encounter = encounterService.getOne(id);
  return encounter;
};

export const patientLoader = async ({ params }: any) => {
  if (WORK_OFFLINE) {
    return defer({ encounter: mockEncounter01 as unknown as PatientEncounter });
  }
  const { id } = params;
  const encounter = encounterService.getOne(id);
  return defer({ encounter });
};

const usePatientEncounter = ():{
  encounter:PatientEncounter,
  tasks:Task[],
  handleEncounterChange:(path: string, value: unknown) => void,
  handleTasksChange:(newTasks: Task[]) => void,
  canEncounterBeChanged:() => boolean
} => {
  // Encounter should be resolved as we used defer with await
  if (WORK_OFFLINE) {
    return {
      encounter: mockEncounter01 as unknown as PatientEncounter,
      tasks: (mockEncounter01.tasks || []) as Task[],
      handleEncounterChange: (_path: string, _value: any) => { console.log("handleEncounterChange called in offline mode"); },
      handleTasksChange: (_newTasks: Task[]) => { console.log("handleTasksChange called in offline mode"); },
      canEncounterBeChanged: () => false, // Or a sensible default for offline mode
    };
  }
  // Original implementation
  const initial = useAsyncValue() as PatientEncounter;

  // Loader only as initial data, then use the hook to allow data updates
  const { data } = encounterService.useGetOne(initial.id, {
    initialData: initial,
  });
  const [encounterUpdates, setEncounterUpdates] = useState<Partial<PatientEncounter>>({});
  // Won't be undefined as we have initial data
  const encounter = merge(data, encounterUpdates) as PatientEncounter;

  const { mutateAsync: updateEncounter } = encounterService.useUpdate();
  const openPatientRiskModal = usePatientRiskModal();
  // set the risk on first load, then keep track of changes
  const [prevRisk, setPrevRisk] = useState(initial.riskScore?.risk ?? ClinicalRisk.LOW);
  // Keep track of task states locally, so we can change ui instantly and
  // batch the update
  const [tasks, setTasks] = useState<Task[]>(
    encounter.tasks ??
    TaskType.map((type) => ({ type, status: "not_requested" }))
  );

  // Listen to changes in the encounter and update the tasks
  useEffect(() => {
    if (encounter.tasks) {
      setTasks([...encounter.tasks]);
    }
  }, [encounter.tasks, setTasks]);
  const canEncounterBeChanged = useCallback(() => {
    const rulesForBlockChanging = [encounter.status === "discharged"];
    return !rulesForBlockChanging.some((it) => it);
  }, [encounter.status]);
  const updateTasks = useCallback(debounce((newTasks: Task[]) => {
    updateEncounter({ id: encounter.id, payload: { tasks: newTasks } });
  }, 1000), []);
  const handleTasksChange = useCallback((newTasks: Task[]) => {
    if (!canEncounterBeChanged()) return;
    setTasks(newTasks);
    updateTasks(newTasks);
  }, [updateTasks, canEncounterBeChanged]);
  const updateEncounterDebounced = useCallback(debounce(async (updates: Partial<PatientEncounter>) => {
    await updateEncounter({ id: encounter.id, payload: updates });
    setEncounterUpdates({});
  }, 1000), []);
  // batch encounter updates to reduce calls to the server
  const handleEncounterChange = useCallback((path: string, value: unknown) => {
    if (!canEncounterBeChanged()) return;
    setEncounterUpdates((updates) => {
      const newUpdates = set(updates, path, value);
      updateEncounterDebounced(newUpdates);
      return { ...newUpdates };
    });
  }, [updateEncounterDebounced, canEncounterBeChanged]);
  // benefit of useEffect is if we get a risk update via sockets, we will be notified
  useEffect(() => {
    const riskScore = data?.riskScore;
    // if the risk has changed and is not low, alert the user
    if (
      riskScore &&
      riskScore.risk !== prevRisk &&
      riskScore.risk !== ClinicalRisk.LOW
    ) {
      openPatientRiskModal({ score: riskScore });
    }
    if (riskScore) {
      setPrevRisk(riskScore.risk);
    }
  }, [data]);
  return {
    encounter,
    tasks,
    handleEncounterChange,
    handleTasksChange,
    canEncounterBeChanged,
  };
};

// 86ayu53wa - remove this type of printing
// const printButton = () => (
//   <Button
//     variant="outlined"
//     sx={{ marginRight: "1rem" }}
//     startIcon={<PrinterIcon />}
//   >
//     Print observation chart
//   </Button>
// );

// eslint-disable-next-line complexity
export const Patient = () => {
  const [currentTab, setCurrentTab] = useState<TabNames>(TabNames.RECORD);
  const { encounter, tasks, handleEncounterChange, handleTasksChange } =
    usePatientEncounter();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.has("tab"))
      setCurrentTab((searchParams.get("tab") as TabNames) || TabNames.RECORD);
  }, []);

  const componentRef = React.useRef<HTMLTableElement>(null);

  const { patient } = encounter;
  const canEdit = !["disabled", "discharged"].includes(encounter.status);

  return (
    <Stack flex={1} sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 3, py: 2 }}>
        <Button
          css={styles.backButton}
          component={NavLink}
          to="/patients"
          startIcon={<ArrowleftIcon />}
        >
          Back to Patients
        </Button>
      </Box>

      {/* Tab Buttons Wrapper */}
      <Box sx={{ position: "relative" }}>
        <ToggleButtonGroup value={currentTab} sx={styles.tabButtonsWrapper}>
          <ToggleButton
            sx={{ gap: 1 }}
            value={TabNames.RECORD}
            onClick={() => setCurrentTab(TabNames.RECORD)}
          >
            <CompareIcon />
            <Text noWrap>Patient record</Text>
          </ToggleButton>
          <ToggleButton
            sx={{ gap: 1 }}
            value={TabNames.OBS_CHART}
            onClick={() => setCurrentTab(TabNames.OBS_CHART)}
          >
            <HeartIcon />
            <Text noWrap>Observations chart</Text>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Stack
        gap={4}
        sx={{ px: 5, py: 3 }}
        css={styles.container}
        className={scrollable}
      >
        {/* Header/Patient Details */}
        <Stack direction="row" justifyContent="space-between">
          <PatientHeader encounter={encounter} />
          <Stack direction="row" justifyContent="end" alignItems="end" gap={1}>
            <PatientEncounterComment encounter={encounter} />
            <PatientNextObservation encounter={encounter} onEncounterChange={handleEncounterChange} />
            {currentTab === TabNames.RECORD && (
              <>
                {/* <ExportRecordButton encounter={encounter} /> */}
                <PatientSplitActions encounter={encounter} />
                <SaveObservationsSplitActions encounter={encounter} />
              </>
            )}
            {currentTab === TabNames.OBS_CHART && (
              <>
                {/*
                  86ayu53wa - remove this type of printing
                  <ReactToPrint
                    trigger={printButton}
                    content={() => componentRef.current}
                  />
                */}
                <PatientObservationSplitActions encounter={encounter} />
                <SaveObservationsSplitActions encounter={encounter} />
              </>
            )}
          </Stack>
        </Stack>

        {/* Main Content */}
        <Stack direction="row" gap={3}>
          <PatientDetailBlock
            onEncounterChange={handleEncounterChange}
            encounter={encounter}
          />
          <Stack flex={3} sx={{ minWidth: 0 }}>
            {currentTab === TabNames.RECORD && (
              <>
                <TabSection first>
                  <PatientDetailsTabs
                    onEncounterChange={handleEncounterChange}
                    encounter={encounter}
                  />
                </TabSection>

                <TabSection>
                  {canEdit && (
                    <TabSubSection>
                      <PatientNotes encounter={encounter} />
                    </TabSubSection>
                  )}

                  <TabSubSection>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Text variant="h4">Tasks</Text>
                      <PatientTasks
                        onChange={handleTasksChange}
                        value={tasks}
                        mode="condensed"
                        disabled={!canEdit}
                      />
                    </Stack>
                  </TabSubSection>
                  <TabSubSection>
                    <PatientActivitiesSection encounter={encounter} />
                  </TabSubSection>
                </TabSection>
              </>
            )}

            {currentTab === TabNames.OBS_CHART && (
              <WithObsTabsCtx>
                <PatientObservationTabs
                  encounter={encounter}
                  handleTasksChange={handleTasksChange}
                  tasks={tasks}
                  patient={patient}
                  ref={componentRef}
                />
              </WithObsTabsCtx>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const loadingEncounter = <AppLoading>Loading patient encounter...</AppLoading>;

const EncounterLoadError = () => {
  const error = useAsyncError();
  const isActAs = isActAsUnauthorisedError(error);

  return (
    <Box p={4} textAlign="center">
      <Text variant="h5">
        {isActAs
          ? "The act-as session failed. Please re-authenticate and then try reloading the page."
          : "Failed to load patient encounter."}
      </Text>
      <Button variant="outlined" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
        Reload
      </Button>
    </Box>
  );
};

export const AwaitPatient = () => {
  const { encounter } = useLoaderData() as {
    encounter: Promise<PatientEncounter>;
  };
  return (
    <React.Suspense fallback={loadingEncounter}>
      <Await resolve={encounter} errorElement={<EncounterLoadError />}>
        <Patient />
      </Await>
    </React.Suspense>
  );
};
